import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Space, Button, Input, Select, message, Typography,
  Rate, Popconfirm, Modal, Descriptions,
} from 'antd';
import {
  SearchOutlined, CheckOutlined, CloseOutlined, DeleteOutlined,
  EyeOutlined, ReloadOutlined,
} from '@ant-design/icons';
import { adminReviewsAPI } from '../services/api';
import { useLanguage } from '../store/LanguageContext';
import type { Review } from '../types';

const { Title, Text } = Typography;

const AdminReviewPage: React.FC = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [approvedFilter, setApprovedFilter] = useState<string | undefined>();
  const [detailModal, setDetailModal] = useState<{ visible: boolean; review?: Review }>({ visible: false });

  const fetchReviews = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params: any = { page, limit: pagination.limit };
      if (search) params.search = search;
      if (approvedFilter !== undefined) params.is_approved = approvedFilter;
      const res = await adminReviewsAPI.getAll(params);
      setReviews(res.data.reviews);
      setPagination(prev => ({
        ...prev,
        page: res.data.pagination.page,
        total: res.data.pagination.total,
      }));
    } catch {
      message.error(t('adminPanel.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [search, approvedFilter, pagination.limit, t]);

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApprove = async (id: number, approved: boolean) => {
    try {
      await adminReviewsAPI.updateApproval(id, approved);
      message.success(t('adminPanel.updateSuccess'));
      fetchReviews(pagination.page);
    } catch {
      message.error(t('adminPanel.updateFailed'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await adminReviewsAPI.delete(id);
      message.success(t('adminPanel.deleteSuccess'));
      fetchReviews(pagination.page);
    } catch {
      message.error(t('adminPanel.deleteFailed'));
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: t('adminPanel.colTool'),
      key: 'tool',
      render: (_: any, record: Review) => (
        <Text>{record.tool?.name || `Tool #${record.tool_id}`}</Text>
      ),
    },
    {
      title: t('adminPanel.colUser'),
      key: 'user',
      render: (_: any, record: Review) => (
        <Text>{record.user?.username || `User #${record.user_id}`}</Text>
      ),
    },
    {
      title: t('adminPanel.colRating'),
      dataIndex: 'rating',
      width: 160,
      render: (rating: number) => <Rate disabled value={rating} style={{ fontSize: 14 }} />,
    },
    {
      title: t('adminPanel.colComment'),
      dataIndex: 'comment',
      ellipsis: true,
      width: 300,
    },
    {
      title: t('adminPanel.colApproved'),
      dataIndex: 'is_approved',
      width: 100,
      render: (approved: boolean) => (
        <Tag color={approved ? 'green' : 'orange'}>
          {approved ? t('adminPanel.approved') : t('adminPanel.pending')}
        </Tag>
      ),
    },
    {
      title: t('adminPanel.colCreatedAt'),
      dataIndex: 'created_at',
      width: 120,
      render: (v: string) => new Date(v).toLocaleDateString(),
    },
    {
      title: t('admin.colActions'),
      key: 'actions',
      width: 180,
      render: (_: any, record: Review) => (
        <Space size="small">
          <Button size="small" icon={<EyeOutlined />} onClick={() => setDetailModal({ visible: true, review: record })} />
          {!record.is_approved && (
            <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => handleApprove(record.id, true)}>
              {t('adminPanel.approve')}
            </Button>
          )}
          {record.is_approved && (
            <Button size="small" icon={<CloseOutlined />} onClick={() => handleApprove(record.id, false)}>
              {t('adminPanel.reject')}
            </Button>
          )}
          <Popconfirm title={t('adminPanel.confirmDelete')} onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3} style={{ marginBottom: 24 }}>{t('adminPanel.reviewManagement')}</Title>

      <Card style={{ borderRadius: 12, marginBottom: 16 }} bodyStyle={{ padding: '16px 24px' }}>
        <Space wrap>
          <Input
            placeholder={t('adminPanel.searchReviews')}
            prefix={<SearchOutlined />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onPressEnter={() => fetchReviews(1)}
            style={{ width: 240 }}
            allowClear
          />
          <Select
            placeholder={t('adminPanel.colApproved')}
            value={approvedFilter}
            onChange={v => setApprovedFilter(v)}
            allowClear
            style={{ width: 140 }}
            options={[
              { label: t('adminPanel.approved'), value: 'true' },
              { label: t('adminPanel.pending'), value: 'false' },
            ]}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={() => fetchReviews(1)}>
            {t('common.search')}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => { setSearch(''); setApprovedFilter(undefined); fetchReviews(1); }}>
            {t('tools.resetFilter')}
          </Button>
        </Space>
      </Card>

      <Card style={{ borderRadius: 12 }}>
        <Table
          loading={loading}
          dataSource={reviews}
          columns={columns}
          rowKey="id"
          scroll={{ x: 1000 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showTotal: total => t('admin.totalItems').replace('{total}', String(total)),
            onChange: page => fetchReviews(page),
          }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={t('adminPanel.reviewDetail')}
        open={detailModal.visible}
        onCancel={() => setDetailModal({ visible: false })}
        footer={null}
        width={560}
      >
        {detailModal.review && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID">{detailModal.review.id}</Descriptions.Item>
            <Descriptions.Item label={t('adminPanel.colTool')}>
              {detailModal.review.tool?.name || `Tool #${detailModal.review.tool_id}`}
            </Descriptions.Item>
            <Descriptions.Item label={t('adminPanel.colUser')}>
              {detailModal.review.user?.username || `User #${detailModal.review.user_id}`}
            </Descriptions.Item>
            <Descriptions.Item label={t('adminPanel.colRating')}>
              <Rate disabled value={detailModal.review.rating} />
            </Descriptions.Item>
            {detailModal.review.title && (
              <Descriptions.Item label={t('adminPanel.colTitle')}>
                {detailModal.review.title}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('adminPanel.colComment')}>
              {detailModal.review.comment}
            </Descriptions.Item>
            <Descriptions.Item label={t('adminPanel.colApproved')}>
              <Tag color={detailModal.review.is_approved ? 'green' : 'orange'}>
                {detailModal.review.is_approved ? t('adminPanel.approved') : t('adminPanel.pending')}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('adminPanel.colCreatedAt')}>
              {new Date(detailModal.review.created_at).toLocaleString()}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default AdminReviewPage;
